import {
  createRoutesFromElements,
  Navigate,
  Outlet,
  redirect,
  Route,
} from "react-router-dom"

import { AuthGuard } from "~/components/auth/AuthGuard"
import { SupplierAppShell } from "~/components/layout/SupplierAppShell"
import { sentryCreateBrowserRouter } from "~/instrument"
import { AdminWrapper } from "~/pages/Admin"
import { Exports } from "~/pages/Admin/Exports"
import { IngestionClientSettingsLayout } from "~/pages/Admin/IngestionSettings"
import { MappingWrapper } from "~/pages/Admin/Mapping"
import { Profile } from "~/pages/Auth/Profile"
import { CertificatesTableProvider } from "~/pages/Brand/Certificates/provider/CertificatesTableProvider"
import { CommunicationStudioLoader } from "~/pages/Brand/CommunicationStudio/CommunicationStudioLoader"
import { Components } from "~/pages/Brand/CommunicationStudio/Content"
import { DesignStudio } from "~/pages/Brand/CommunicationStudio/DesignStudio"
import { SearchEnginePreview } from "~/pages/Brand/CommunicationStudio/DesignStudio/components/SearchEnginePreview"
import { Languages } from "~/pages/Brand/CommunicationStudio/Languages"
import { OrganizationGeneralSettings } from "~/pages/Brand/CommunicationStudio/OrganizationGeneralSettings"
import { DataTransferExport } from "~/pages/Brand/DataTransfer/Export"
import { DataTransferImport } from "~/pages/Brand/DataTransfer/Import"
import { FAQ } from "~/pages/Brand/FAQ/FAQ.tsx"
import { HomeWrapper } from "~/pages/Brand/Home/HomeWrapper.tsx"
import { InvoicesWrapper } from "~/pages/Brand/Invoices"
import { ProductDetailsWrapper } from "~/pages/Brand/Products/Details"
import { ProductDetails } from "~/pages/Brand/Products/Details/ProductDetails"
import { ProductsListWrapper } from "~/pages/Brand/Products/List"
import { ProductTableFactory } from "~/pages/Brand/Products/List/Components/ProductsTables/ProductTableFactory"
import { publicAgecSearchPath } from "~/pages/Brand/Products/List/constants"
import { ProductsAGECWrapper } from "~/pages/Brand/Regulations/AGEC"
import { ProductsLCAWrapper } from "~/pages/Brand/Regulations/LCA"
import { ProductsRDUEWrapper } from "~/pages/Brand/Regulations/RDUE"
import { SupplierMonitoringWrapper } from "~/pages/Brand/Suppliers/List/SupplierMonitoringWrapper"
import { SuppliersList } from "~/pages/Brand/Suppliers/List/SuppliersList"
import { SuppliersSettings } from "~/pages/Brand/Suppliers/SuppliersRepository"
import { BrandAppShell } from "~/pages/BrandAppShell.tsx"
import { PublicSearchEnginePage } from "~/pages/Public"
import { REDIRECT_MAP } from "~/pages/Public/utils"
import { SupplierContacts } from "~/pages/SupplierPortal/Contacts/SupplierContacts.tsx"
import { SupplierFAQWrapper } from "~/pages/SupplierPortal/FAQ"
import { SupplierFAQ } from "~/pages/SupplierPortal/FAQ/SupplierFAQ.tsx"
import { SupplierProductionSitesWrapper } from "~/pages/SupplierPortal/ProductionSites"
import { SupplierProductionSites } from "~/pages/SupplierPortal/ProductionSites/SupplierProductionSites.tsx"
import { SubSupplierMonitoring } from "~/pages/SupplierPortal/SubSupplierMonitoring"

import { AdminAuthGuard } from "./Admin/AdminAuthGuard"
import { FAQAdminWrapper } from "./Admin/FAQ"
import { Ingestion } from "./Admin/Ingestion"
import { InvoicesAdminWrapper } from "./Admin/Invoices"
import { MasterLists } from "./Admin/MasterLists"
import { OrganizationsAdmin } from "./Admin/Organizations"
import { OrganizationDetails } from "./Admin/Organizations/OrganizationDetails"
import { OrganizationDetailsWrapper } from "./Admin/Organizations/OrganizationDetailsWrapper"
import { SecurityAdmin } from "./Admin/Security"
import { TranslationsAdminWrapper } from "./Admin/Translations"
import { Login } from "./Auth/Login"
import { MagicLink } from "./Auth/MagicLink"
import { CertificatesListWrapper } from "./Brand/Certificates"
import { ProductSheetPreview } from "./Brand/CommunicationStudio/DesignStudio/components/ProductSheetPreview"
import { DataTransferWrapper } from "./Brand/DataTransfer"
import { FAQWrapper } from "./Brand/FAQ"
import { Home } from "./Brand/Home"
import { RegulationsWrapper } from "./Brand/Regulations"
import { SuppliersListWrapper } from "./Brand/Suppliers"
import { SupplierContactsWrapper } from "./SupplierPortal/Contacts"
import { TraceabilityRequests } from "./SupplierPortal/TraceabilityRequests"

export const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route element={<Outlet />}>
      <Route path="/magic-link" element={<MagicLink />} />
      <Route path="/login" element={<Login />} />
      <Route
        path={`${publicAgecSearchPath}/:displaySlug`}
        element={<PublicSearchEnginePage />}
        loader={async ({ params }) => {
          // Process redirects for churned partners
          if (params.displaySlug && params.displaySlug in REDIRECT_MAP)
            return redirect(REDIRECT_MAP[params.displaySlug].href)
          return null
        }}
      />
      <Route path="/*" element={<AuthGuard />}>
        <Route path="preview/product-sheet" element={<ProductSheetPreview />} />
        <Route
          path="preview/search-engine/:slug"
          element={<SearchEnginePreview />}
        />
        <Route element={<BrandAppShell />}>
          <Route element={<HomeWrapper />}>
            <Route index element={<Home />} />
          </Route>
          <Route path="products" element={<ProductsListWrapper />}>
            <Route index element={<ProductTableFactory />} />
            <Route path=":view" element={<ProductTableFactory />} />
          </Route>
          <Route
            path="products/details/:productId"
            element={<ProductDetailsWrapper />}
          >
            <Route index element={<ProductDetails />} />
            <Route path="general" element={<ProductDetails />} />
          </Route>
          <Route path="regulations" element={<RegulationsWrapper />}>
            <Route index element={<ProductsAGECWrapper />} />
            <Route path="rdue" element={<ProductsRDUEWrapper />} />
            <Route path="agec" element={<ProductsAGECWrapper />} />
            <Route path="lca" element={<ProductsLCAWrapper />} />
          </Route>
          <Route path="suppliers" element={<SuppliersListWrapper />}>
            <Route index element={<SuppliersList />} />
            <Route path="repository" element={<SuppliersSettings />} />
            <Route path=":tab" element={<SuppliersList />} />
          </Route>
          <Route path="documents" element={<CertificatesListWrapper />}>
            <Route index element={<CertificatesTableProvider />} />
            <Route path=":view" element={<CertificatesTableProvider />} />
          </Route>
          <Route path="data-transfer" element={<DataTransferWrapper />}>
            <Route index element={<DataTransferImport />} />
            <Route path="import" index element={<DataTransferImport />} />
            <Route path="export" element={<DataTransferExport />} />
          </Route>
          <Route path="faq" element={<FAQWrapper />}>
            <Route index element={<FAQ />} />
          </Route>
          <Route
            path="communication-studio"
            element={<CommunicationStudioLoader />}
          >
            <Route index element={<Navigate to="general" replace />} />
            <Route path="general" element={<OrganizationGeneralSettings />} />
            <Route path="languages" element={<Languages />} />
            <Route path="design" element={<DesignStudio />} />
            <Route path="content" element={<Components />} />
          </Route>
          <Route path="admin" element={<AdminAuthGuard />}>
            <Route element={<AdminWrapper />}>
              <Route index element={<OrganizationsAdmin />} />
              <Route path="organizations" element={<OrganizationsAdmin />} />
              <Route path="security" element={<SecurityAdmin />} />
              <Route path="faq" element={<FAQAdminWrapper />} />
              <Route
                path="translations"
                element={<TranslationsAdminWrapper />}
              />
              <Route path="masterlists" element={<MasterLists />} />
              <Route path="ingestion" element={<Ingestion />} />
              <Route path="admin-exports" element={<Exports />} />
              <Route path="mapping" element={<MappingWrapper />} />
              <Route
                path="ingestion-settings"
                element={<IngestionClientSettingsLayout />}
              />
              <Route path="mapping" element={<MappingWrapper />} />
              <Route
                path="ingestion-settings"
                element={<IngestionClientSettingsLayout />}
              />
            </Route>
            <Route
              path="organizations/:organizationId"
              element={<OrganizationDetailsWrapper />}
            >
              <Route index element={<OrganizationDetails />} />
              <Route path="general" element={<OrganizationDetails />} />
              <Route path="invoices" element={<InvoicesAdminWrapper />} />
            </Route>
          </Route>
          <Route path="invoices" element={<InvoicesWrapper />} />
          <Route path="profile" element={<Profile />} />
        </Route>
        {/* SUPPLIER */}
        <Route path="supplier" element={<SupplierAppShell />}>
          <Route
            path="requests/:requestType?"
            element={<TraceabilityRequests />}
          />
          <Route element={<SubSupplierMonitoring />}>
            <Route
              path="traceability"
              element={<SupplierMonitoringWrapper type="subSupplier" />}
            />
          </Route>
          <Route element={<SupplierProductionSitesWrapper />}>
            <Route
              path="production-sites"
              element={<SupplierProductionSites />}
            />
          </Route>
          <Route element={<SupplierContactsWrapper />}>
            <Route path="contacts" element={<SupplierContacts />} />
          </Route>
          <Route path="faq" element={<SupplierFAQWrapper />}>
            <Route index element={<SupplierFAQ />} />
          </Route>
        </Route>
      </Route>
    </Route>
  )
)
